import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import remarkBreaks from 'remark-breaks';
import {
  ArrowLeft,
  Trash2,
  RefreshCw,
  AlertTriangle,
  Wrench,
  Loader2,
  Copy,
  Check,
  Download,
  ThumbsUp,
  ThumbsDown,
} from 'lucide-react';
import { fetchInspectionById, deleteInspection, reEnrichInspection, submitTranscriptFeedback } from '../services/inspectionService';
import { usePlaybackRawSetting, useExtendedErrorReportsSetting } from '../hooks/useSetting';
import AppBarMenu from './AppBarMenu';
import Logo from './Logo';
import AudioWaveformPlayer from './AudioWaveformPlayer';

// Reusable Dialog component
const Dialog = ({ open, onClose, title, children, actions }) => {
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-surface rounded-lg shadow-lg max-w-sm w-full p-6">
        <h2 className="text-lg font-semibold text-text mb-2">{title}</h2>
        <div className="text-text-muted mb-6">{children}</div>
        <div className="flex justify-end gap-3">
          {actions}
        </div>
      </div>
    </div>
  );
};

// Alert component
const Alert = ({ severity, children, className = '' }) => {
  const styles = {
    error: 'bg-red-50 border-red-200 text-red-800',
    warning: 'bg-amber-50 border-amber-200 text-amber-800',
    success: 'bg-green-50 border-green-200 text-green-800',
    info: 'bg-blue-50 border-blue-200 text-blue-800',
  };

  const icons = {
    error: AlertTriangle,
    warning: AlertTriangle,
    success: null,
    info: null,
  };

  const Icon = icons[severity];

  return (
    <div className={`px-4 py-3 border rounded-lg flex items-center gap-2 ${styles[severity]} ${className}`}>
      {Icon && <Icon size={18} />}
      <span>{children}</span>
    </div>
  );
};

// Map backend error messages to error categories
const getErrorCategory = (inspection) => {
  if (!inspection) return null;

  const status = (inspection.status || '').toUpperCase();
  const errorMessage = (inspection.errorMessage || '').toLowerCase();

  // Only show errors for failed status
  if (!status.includes('FAILED') && !status.includes('ERROR')) {
    return null;
  }

  // Map error patterns to categories
  if (errorMessage.includes('preprocessing') || errorMessage.includes('silence')) {
    return 'client_preprocessing';
  }
  if (errorMessage.includes('upload') || errorMessage.includes('s3')) {
    return 'upload_failed';
  }
  if (errorMessage.includes('ffmpeg') || errorMessage.includes('concatenat')) {
    return 'backend_processing';
  }
  if (errorMessage.includes('transcrib') || errorMessage.includes('audio_too_short') || errorMessage.includes('too short')) {
    return 'transcription_failed';
  }
  if (errorMessage.includes('bedrock') || errorMessage.includes('llm') || errorMessage.includes('enrich')) {
    return 'enrichment_failed';
  }
  if (errorMessage.includes('timeout') || errorMessage.includes('lambda')) {
    return 'timeout';
  }

  return 'unknown';
};

const InspectionDetail = ({ onLogout, showMessage, embedded = false, onDelete, isAdmin = false }) => {
  const { id: inspectionId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [inspection, setInspection] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [audioUrl, setAudioUrl] = useState(null);
  const [rawAudioUrl, setRawAudioUrl] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [reEnriching, setReEnriching] = useState(false);
  const [copiedField, setCopiedField] = useState(null); // 'raw' | 'processed' | null
  const [feedbackSubmitting, setFeedbackSubmitting] = useState(null); // 'raw' | 'processed' | null
  const [playbackRawEnabled] = usePlaybackRawSetting();
  const [extendedErrorReportsEnabled] = useExtendedErrorReportsSetting();

  // Get current feedback state from inspection
  const rawFeedback = inspection?.transcriptFeedback?.rawTranscript?.rating;
  const processedFeedback = inspection?.transcriptFeedback?.processedTranscript?.rating;

  const handleFeedback = async (transcriptType, rating) => {
    // If clicking the same rating, treat as toggle off (no action for now, could add remove feedback)
    const currentRating = transcriptType === 'raw' ? rawFeedback : processedFeedback;
    if (currentRating === rating) return;

    try {
      setFeedbackSubmitting(transcriptType);
      await submitTranscriptFeedback(inspectionId, transcriptType, rating);

      // Update local state to reflect the new feedback
      setInspection(prev => ({
        ...prev,
        transcriptFeedback: {
          ...prev?.transcriptFeedback,
          [transcriptType === 'raw' ? 'rawTranscript' : 'processedTranscript']: {
            rating,
            submittedAt: new Date().toISOString(),
          }
        }
      }));
    } catch (err) {
      console.error('Error submitting feedback:', err);
      showMessage?.(t('common.error'), 'error');
    } finally {
      setFeedbackSubmitting(null);
    }
  };

  const handleCopy = async (text, field) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleDownloadAudio = async (url, isRaw = false) => {
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      const vehicleId = inspection?.vehicleId || 'audio';
      const suffix = isRaw ? '_raw' : '';
      link.download = `${vehicleId}${suffix}.webm`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
    } catch (err) {
      console.error('Failed to download audio:', err);
      showMessage?.(t('common.error'), 'error');
    }
  };

  useEffect(() => {
    loadInspection();
  }, [inspectionId]);

  const loadInspection = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await fetchInspectionById(inspectionId);
      setInspection(data);

      if (data.audioUrl) {
        setAudioUrl(data.audioUrl);
      }
      if (data.rawAudioUrl) {
        console.log('Setting rawAudioUrl:', data.rawAudioUrl);
        setRawAudioUrl(data.rawAudioUrl);
      } else {
        console.log('No rawAudioUrl in response, data:', data);
      }
    } catch (err) {
      console.error('Error loading inspection:', err);
      setError(err.message || t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate('/');
  };

  const handleDeleteClick = () => {
    setDeleteDialogOpen(true);
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
  };

  const handleDeleteConfirm = async () => {
    try {
      setError('');
      await deleteInspection(inspectionId);
      setDeleteDialogOpen(false);
      showMessage?.(t('list.delete_success'), 'success');
      if (onDelete) {
        onDelete(inspectionId);
      } else {
        navigate('/');
      }
    } catch (err) {
      console.error('Error deleting inspection:', err);
      setError(err.message || t('common.error'));
      setDeleteDialogOpen(false);
    }
  };

  const handleReEnrich = async () => {
    try {
      setReEnriching(true);
      setError('');

      const result = await reEnrichInspection(inspectionId);

      setInspection({
        ...inspection,
        processedTranscript: result.processedTranscript,
      });

      showMessage?.(t('detail.re_enrich_success'), 'success');
    } catch (err) {
      console.error('Error re-enriching inspection:', err);
      setError(err.message || t('common.error'));
    } finally {
      setReEnriching(false);
    }
  };

  const formatDate = (isoString) => {
    // Backend sends UTC timestamps without 'Z' suffix - append it if missing
    const utcString = isoString.endsWith('Z') ? isoString : isoString + 'Z';
    const date = new Date(utcString);
    return date.toLocaleString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Normalize backend status values to display values
  const normalizeStatus = (backendStatus) => {
    if (!backendStatus) return 'PENDING';
    const upper = backendStatus.toUpperCase();
    // Map various failure statuses to FAILED
    if (upper.includes('FAILED') || upper.includes('ERROR')) {
      return 'FAILED';
    }
    // Map PROCESSING to TRANSCRIBING (same conceptual state)
    if (upper === 'PROCESSING') {
      return 'TRANSCRIBING';
    }
    return backendStatus;
  };

  const getStatusBadge = (status) => {
    const normalizedStatus = normalizeStatus(status);
    const styles = {
      COMPLETED: 'badge-success',
      TRANSCRIBING: 'badge-warning',
      FAILED: 'badge-error',
      UPLOADED: 'badge-info',
    };

    return (
      <span className={`badge ${styles[normalizedStatus] || 'badge-info'}`}>
        {t(`status.${normalizedStatus.toLowerCase()}`, normalizedStatus)}
      </span>
    );
  };

  const getBewertungBadge = (bewertung) => {
    const styles = {
      BESTANDEN: 'badge-success',
      NICHT_BESTANDEN: 'badge-error',
      NACHPRUEFUNG_ERFORDERLICH: 'badge-warning',
    };

    const labels = {
      BESTANDEN: 'Bestanden',
      NICHT_BESTANDEN: 'Nicht bestanden',
      NACHPRUEFUNG_ERFORDERLICH: 'Nachprüfung erforderlich',
    };

    return (
      <span className={`badge ${styles[bewertung] || 'badge-info'} font-bold`}>
        {labels[bewertung] || bewertung}
      </span>
    );
  };

  // Loading state
  if (loading) {
    if (embedded) {
      return (
        <div className="flex justify-center py-16">
          <Loader2 className="w-8 h-8 text-primary animate-spin" />
        </div>
      );
    }
    return (
      <div className="min-h-screen bg-background">
        <header className="app-bar">
          <button onClick={handleBack} className="p-2 text-white hover:opacity-80 cursor-pointer">
            <ArrowLeft size={24} />
          </button>
          <Logo size={40} />
          <h1 className="flex-1 text-white font-normal">{t('detail.title')}</h1>
        </header>
        <div className="flex justify-center py-16">
          <Loader2 className="w-8 h-8 text-primary animate-spin" />
        </div>
      </div>
    );
  }

  // Error state (no inspection loaded)
  if (error && !inspection) {
    if (embedded) {
      return (
        <main className="max-w-lg mx-auto px-4 py-6">
          <Alert severity="error">{error}</Alert>
        </main>
      );
    }
    return (
      <div className="min-h-screen bg-background">
        <header className="app-bar">
          <button onClick={handleBack} className="p-2 text-white hover:opacity-80 cursor-pointer">
            <ArrowLeft size={24} />
          </button>
          <Logo size={40} />
          <h1 className="flex-1 text-white font-normal">{t('common.error')}</h1>
        </header>
        <main className="max-w-lg mx-auto px-4 py-6">
          <Alert severity="error">{error}</Alert>
        </main>
      </div>
    );
  }

  const report = inspection?.structuredReport || {};

  // Embedded mode - no app bar, simplified layout
  if (embedded) {
    return (
      <div className="bg-background h-full">
        {/* Back button for mobile in embedded mode */}
        <div className="lg:hidden p-4 border-b border-border">
          <button onClick={handleBack} className="flex items-center gap-2 text-primary hover:opacity-80 cursor-pointer">
            <ArrowLeft size={20} />
            <span>{t('common.back')}</span>
          </button>
        </div>

        {/* Partial transcript warning */}
        {inspection?.transcriptionResult?.isPartial && (
          <Alert severity="warning" className="rounded-none">
            {t('detail.partial_message')}
          </Alert>
        )}

        {/* Main Content */}
        <main className="w-full px-4 lg:px-8 py-6 space-y-4">
          {error && (
            <Alert severity="error">{error}</Alert>
          )}

          {/* Vehicle ID and Status */}
          <div className="card-elevated p-4">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-3xl font-bold font-mono text-secondary">
                  {inspection?.title || inspection?.vehicleId || t('list.untitled')}
                </h2>
                <p className="text-sm text-text-muted mt-2">
                  {inspection?.createdAt && formatDate(inspection.createdAt)}
                  <span className="font-mono ml-2">#{inspection?.inspectionId?.slice(-7)}</span>
                </p>
              </div>
              <div className="flex-shrink-0">
                {inspection?.status && getStatusBadge(inspection.status)}
              </div>
            </div>
            {report.bewertung && (
              <div className="mt-3">
                {getBewertungBadge(report.bewertung)}
              </div>
            )}
            {/* Error Alert */}
            {getErrorCategory(inspection) && (
              <div className="mt-3">
                <Alert severity="error">
                  <div>
                    <p>{t(`errors.${getErrorCategory(inspection)}`)}</p>
                    {extendedErrorReportsEnabled && inspection.errorMessage && (
                      <p className="text-xs mt-1 opacity-75 font-mono">
                        [{inspection.errorMessage}]
                      </p>
                    )}
                  </div>
                </Alert>
              </div>
            )}
          </div>

          {/* Transcript Card */}
          {(audioUrl || inspection?.rawTranscript) && (
            <div className="card-elevated p-4 space-y-4">
              <h3 className="text-lg font-bold text-secondary">
                {t('detail.transcript')}
              </h3>

              {/* Raw Audio (debug) - shown above processed when enabled */}
              {playbackRawEnabled && rawAudioUrl && (
                <div className="pb-3 border-b border-amber-300">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="text-sm font-semibold text-amber-700">
                      {t('detail.raw_audio')}
                    </h4>
                    <button
                      onClick={() => handleDownloadAudio(rawAudioUrl, true)}
                      className="p-2 hover:bg-amber-100 rounded-md transition-colors"
                      title={t('common.download')}
                    >
                      <Download size={18} className="text-amber-700" />
                    </button>
                  </div>
                  <AudioWaveformPlayer src={rawAudioUrl} />
                </div>
              )}

              {/* Processed Audio */}
              {audioUrl && (
                <div>
                  <div className="flex items-center justify-end mb-2">
                    <button
                      onClick={() => handleDownloadAudio(audioUrl, false)}
                      className="p-2 hover:bg-background rounded-md transition-colors"
                      title={t('common.download')}
                    >
                      <Download size={18} className="text-text-muted" />
                    </button>
                  </div>
                  <AudioWaveformPlayer src={audioUrl} />
                </div>
              )}

              {/* Raw Transcript */}
              {inspection?.rawTranscript && (
                <div className={audioUrl ? 'pt-3 border-t border-border' : ''}>
                  <div className="flex items-center justify-end gap-1 mb-2">
                    <button
                      onClick={() => handleFeedback('raw', 'like')}
                      disabled={feedbackSubmitting === 'raw'}
                      className={`p-2 rounded-md transition-colors ${
                        rawFeedback === 'like'
                          ? 'bg-green-100 text-green-600'
                          : 'hover:bg-background text-text-muted'
                      }`}
                      title={t('detail.feedback_like')}
                    >
                      {feedbackSubmitting === 'raw' ? (
                        <Loader2 size={18} className="animate-spin" />
                      ) : (
                        <ThumbsUp size={18} className={rawFeedback === 'like' ? 'fill-current' : ''} />
                      )}
                    </button>
                    <button
                      onClick={() => handleFeedback('raw', 'dislike')}
                      disabled={feedbackSubmitting === 'raw'}
                      className={`p-2 rounded-md transition-colors ${
                        rawFeedback === 'dislike'
                          ? 'bg-red-100 text-red-600'
                          : 'hover:bg-background text-text-muted'
                      }`}
                      title={t('detail.feedback_dislike')}
                    >
                      <ThumbsDown size={18} className={rawFeedback === 'dislike' ? 'fill-current' : ''} />
                    </button>
                    <button
                      onClick={() => handleCopy(inspection.rawTranscript, 'raw')}
                      className="p-2 hover:bg-background rounded-md transition-colors"
                      title={t('common.copy')}
                    >
                      {copiedField === 'raw' ? (
                        <Check size={18} className="text-success" />
                      ) : (
                        <Copy size={18} className="text-text-muted" />
                      )}
                    </button>
                  </div>
                  <div className="max-h-64 overflow-y-auto">
                    <p className="text-sm text-text-muted italic leading-relaxed">
                      {inspection.rawTranscript}
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Summary */}
          {report.zusammenfassung && (
            <div className="card-elevated p-4">
              <h3 className="text-lg font-bold text-secondary mb-3">
                Zusammenfassung
              </h3>
              <p className="text-text leading-relaxed">
                {report.zusammenfassung}
              </p>
            </div>
          )}

          {/* Defects */}
          {report.maengel && report.maengel.length > 0 && (
            <div className="card-elevated p-4">
              <h3 className="text-lg font-bold text-secondary mb-3">
                Mängel
              </h3>
              <ul className="space-y-3">
                {report.maengel.map((mangel, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <AlertTriangle
                      size={20}
                      className={`flex-shrink-0 mt-0.5 ${mangel.kategorie === 'KRITISCH' ? 'text-error' : 'text-warning'}`}
                    />
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-semibold">{mangel.beschreibung}</span>
                        <span className={`badge ${mangel.kategorie === 'KRITISCH' ? 'badge-error' : 'badge-warning'}`}>
                          {mangel.kategorie}
                        </span>
                      </div>
                      {mangel.ort && (
                        <p className="text-sm text-text-muted">Ort: {mangel.ort}</p>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Safety Concerns */}
          {report.sicherheitsbedenken && report.sicherheitsbedenken.length > 0 && (
            <div className="card-elevated p-4 bg-amber-50 border-amber-200">
              <h3 className="text-lg font-bold text-amber-800 mb-3">
                Sicherheitsbedenken
              </h3>
              <ul className="space-y-2">
                {report.sicherheitsbedenken.map((bedenken, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <AlertTriangle size={20} className="flex-shrink-0 mt-0.5 text-amber-700" />
                    <span className="text-amber-900">{bedenken}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Maintenance Recommendations */}
          {report.wartungsempfehlungen && report.wartungsempfehlungen.length > 0 && (
            <div className="card-elevated p-4">
              <h3 className="text-lg font-bold text-secondary mb-3">
                Wartungsempfehlungen
              </h3>
              <ul className="space-y-2">
                {report.wartungsempfehlungen.map((empfehlung, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <Wrench size={20} className="flex-shrink-0 mt-0.5 text-blue-500" />
                    <span>{empfehlung}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Additional Remarks */}
          {report.bemerkungen && (
            <div className="card-elevated p-4">
              <h3 className="text-lg font-bold text-secondary mb-3">
                Bemerkungen
              </h3>
              <p className="text-text leading-relaxed">
                {report.bemerkungen}
              </p>
            </div>
          )}

          {/* Processed Transcript */}
          {inspection?.processedTranscript && (
            <div className="card-elevated p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-lg font-bold text-secondary">
                  {t('detail.processed_transcript')}
                </h3>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => handleFeedback('processed', 'like')}
                    disabled={feedbackSubmitting === 'processed'}
                    className={`p-2 rounded-md transition-colors ${
                      processedFeedback === 'like'
                        ? 'bg-green-100 text-green-600'
                        : 'hover:bg-background text-text-muted'
                    }`}
                    title={t('detail.feedback_like')}
                  >
                    {feedbackSubmitting === 'processed' ? (
                      <Loader2 size={18} className="animate-spin" />
                    ) : (
                      <ThumbsUp size={18} className={processedFeedback === 'like' ? 'fill-current' : ''} />
                    )}
                  </button>
                  <button
                    onClick={() => handleFeedback('processed', 'dislike')}
                    disabled={feedbackSubmitting === 'processed'}
                    className={`p-2 rounded-md transition-colors ${
                      processedFeedback === 'dislike'
                        ? 'bg-red-100 text-red-600'
                        : 'hover:bg-background text-text-muted'
                    }`}
                    title={t('detail.feedback_dislike')}
                  >
                    <ThumbsDown size={18} className={processedFeedback === 'dislike' ? 'fill-current' : ''} />
                  </button>
                  <button
                    onClick={() => handleCopy(inspection.processedTranscript, 'processed')}
                    className="p-2 hover:bg-background rounded-md transition-colors"
                    title={t('common.copy')}
                  >
                    {copiedField === 'processed' ? (
                      <Check size={18} className="text-success" />
                    ) : (
                      <Copy size={18} className="text-text-muted" />
                    )}
                  </button>
                </div>
              </div>
              <hr className="border-border mb-3" />
              <div className="max-h-96 overflow-y-auto prose prose-sm max-w-none text-text prose-headings:text-text prose-headings:font-bold prose-p:text-text prose-strong:text-text prose-ul:text-text prose-ol:text-text prose-li:text-text">
                <ReactMarkdown remarkPlugins={[remarkBreaks]}>{inspection.processedTranscript}</ReactMarkdown>
              </div>
            </div>
          )}

          {/* Action Buttons - at bottom */}
          <div className="flex gap-3 pt-4">
            <button
              onClick={handleReEnrich}
              disabled={reEnriching}
              className="btn-secondary flex-1 flex items-center justify-center gap-2"
            >
              {reEnriching ? (
                <Loader2 size={20} className="animate-spin" />
              ) : (
                <RefreshCw size={20} />
              )}
              {reEnriching ? t('detail.re_enriching') : t('detail.re_enrich')}
            </button>
            <button
              onClick={handleDeleteClick}
              className="btn-secondary flex-1 flex items-center justify-center gap-2 border-error text-error hover:bg-red-50"
            >
              <Trash2 size={20} />
              {t('detail.delete')}
            </button>
          </div>
        </main>

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={deleteDialogOpen}
          onClose={handleDeleteCancel}
          title={t('list.delete_confirm')}
          actions={
            <>
              <button onClick={handleDeleteCancel} className="btn-secondary">
                {t('common.cancel')}
              </button>
              <button
                onClick={handleDeleteConfirm}
                className="btn-primary bg-error hover:bg-error-dark"
              >
                {t('common.delete')}
              </button>
            </>
          }
        >
          {inspection && t('list.delete_message', { vehicleId: inspection.vehicleId })}
        </Dialog>
      </div>
    );
  }

  // Standalone mode - full page with app bar
  return (
    <div className="min-h-screen bg-background pb-24">
      {/* App Bar */}
      <header className="app-bar">
        <button onClick={handleBack} className="p-2 text-white hover:opacity-80 cursor-pointer">
          <ArrowLeft size={24} />
        </button>
        <Logo size={40} />
        <h1 className="flex-1 text-white font-normal text-xl">
          {t('detail.title')}
        </h1>
        <button onClick={handleDeleteClick} className="p-2 text-white hover:opacity-80 cursor-pointer">
          <Trash2 size={24} />
        </button>
        <AppBarMenu onLogout={onLogout} isAdmin={isAdmin} />
      </header>

      {/* Partial transcript warning */}
      {inspection?.transcriptionResult?.isPartial && (
        <Alert severity="warning" className="rounded-none">
          {t('detail.partial_message')}
        </Alert>
      )}

      {/* Main Content */}
      <main className="max-w-lg mx-auto px-4 py-6 space-y-4">
        {error && (
          <Alert severity="error">{error}</Alert>
        )}

        {/* Vehicle ID and Status */}
        <div className="card-elevated p-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-3xl font-bold font-mono text-secondary">
                {inspection?.title || inspection?.vehicleId || t('list.untitled')}
              </h2>
              <p className="text-sm text-text-muted mt-2">
                {inspection?.createdAt && formatDate(inspection.createdAt)}
                <span className="font-mono ml-2">#{inspection?.inspectionId?.slice(-7)}</span>
              </p>
            </div>
            <div className="flex-shrink-0">
              {inspection?.status && getStatusBadge(inspection.status)}
            </div>
          </div>
          {report.bewertung && (
            <div className="mt-3">
              {getBewertungBadge(report.bewertung)}
            </div>
          )}
          {/* Error Alert */}
          {getErrorCategory(inspection) && (
            <div className="mt-3">
              <Alert severity="error">
                <div>
                  <p>{t(`errors.${getErrorCategory(inspection)}`)}</p>
                  {extendedErrorReportsEnabled && inspection.errorMessage && (
                    <p className="text-xs mt-1 opacity-75 font-mono">
                      [{inspection.errorMessage}]
                    </p>
                  )}
                </div>
              </Alert>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3">
          <button
            onClick={handleReEnrich}
            disabled={reEnriching}
            className="btn-secondary flex-1 flex items-center justify-center gap-2"
          >
            {reEnriching ? (
              <Loader2 size={20} className="animate-spin" />
            ) : (
              <RefreshCw size={20} />
            )}
            {reEnriching ? t('detail.re_enriching') : t('detail.re_enrich')}
          </button>
          <button
            onClick={handleDeleteClick}
            className="btn-secondary flex-1 flex items-center justify-center gap-2 border-error text-error hover:bg-red-50"
          >
            <Trash2 size={20} />
            {t('detail.delete')}
          </button>
        </div>

        {/* Transcript Card */}
        {(audioUrl || inspection?.rawTranscript) && (
          <div className="card-elevated p-4 space-y-4">
            <h3 className="text-lg font-bold text-secondary">
              {t('detail.transcript')}
            </h3>

            {/* Raw Audio (debug) - shown above processed when enabled */}
            {playbackRawEnabled && rawAudioUrl && (
              <div className="pb-3 border-b border-amber-300">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-sm font-semibold text-amber-700">
                    {t('detail.raw_audio')}
                  </h4>
                  <button
                    onClick={() => handleDownloadAudio(rawAudioUrl, true)}
                    className="p-2 hover:bg-amber-100 rounded-md transition-colors"
                    title={t('common.download')}
                  >
                    <Download size={18} className="text-amber-700" />
                  </button>
                </div>
                <AudioWaveformPlayer src={rawAudioUrl} />
              </div>
            )}

            {/* Processed Audio */}
            {audioUrl && (
              <div>
                <div className="flex items-center justify-end mb-2">
                  <button
                    onClick={() => handleDownloadAudio(audioUrl, false)}
                    className="p-2 hover:bg-background rounded-md transition-colors"
                    title={t('common.download')}
                  >
                    <Download size={18} className="text-text-muted" />
                  </button>
                </div>
                <AudioWaveformPlayer src={audioUrl} />
              </div>
            )}

            {/* Raw Transcript */}
            {inspection?.rawTranscript && (
              <div className={audioUrl ? 'pt-3 border-t border-border' : ''}>
                <div className="flex items-center justify-end gap-1 mb-2">
                  <button
                    onClick={() => handleFeedback('raw', 'like')}
                    disabled={feedbackSubmitting === 'raw'}
                    className={`p-2 rounded-md transition-colors ${
                      rawFeedback === 'like'
                        ? 'bg-green-100 text-green-600'
                        : 'hover:bg-background text-text-muted'
                    }`}
                    title={t('detail.feedback_like')}
                  >
                    {feedbackSubmitting === 'raw' ? (
                      <Loader2 size={18} className="animate-spin" />
                    ) : (
                      <ThumbsUp size={18} className={rawFeedback === 'like' ? 'fill-current' : ''} />
                    )}
                  </button>
                  <button
                    onClick={() => handleFeedback('raw', 'dislike')}
                    disabled={feedbackSubmitting === 'raw'}
                    className={`p-2 rounded-md transition-colors ${
                      rawFeedback === 'dislike'
                        ? 'bg-red-100 text-red-600'
                        : 'hover:bg-background text-text-muted'
                    }`}
                    title={t('detail.feedback_dislike')}
                  >
                    <ThumbsDown size={18} className={rawFeedback === 'dislike' ? 'fill-current' : ''} />
                  </button>
                  <button
                    onClick={() => handleCopy(inspection.rawTranscript, 'raw')}
                    className="p-2 hover:bg-background rounded-md transition-colors"
                    title={t('common.copy')}
                  >
                    {copiedField === 'raw' ? (
                      <Check size={18} className="text-success" />
                    ) : (
                      <Copy size={18} className="text-text-muted" />
                    )}
                  </button>
                </div>
                <div className="max-h-64 overflow-y-auto">
                  <p className="text-sm text-text-muted italic leading-relaxed">
                    {inspection.rawTranscript}
                  </p>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Summary */}
        {report.zusammenfassung && (
          <div className="card-elevated p-4">
            <h3 className="text-lg font-bold text-secondary mb-3">
              Zusammenfassung
            </h3>
            <p className="text-text leading-relaxed">
              {report.zusammenfassung}
            </p>
          </div>
        )}

        {/* Defects */}
        {report.maengel && report.maengel.length > 0 && (
          <div className="card-elevated p-4">
            <h3 className="text-lg font-bold text-secondary mb-3">
              Mängel
            </h3>
            <ul className="space-y-3">
              {report.maengel.map((mangel, index) => (
                <li key={index} className="flex items-start gap-3">
                  <AlertTriangle
                    size={20}
                    className={`flex-shrink-0 mt-0.5 ${mangel.kategorie === 'KRITISCH' ? 'text-error' : 'text-warning'}`}
                  />
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-semibold">{mangel.beschreibung}</span>
                      <span className={`badge ${mangel.kategorie === 'KRITISCH' ? 'badge-error' : 'badge-warning'}`}>
                        {mangel.kategorie}
                      </span>
                    </div>
                    {mangel.ort && (
                      <p className="text-sm text-text-muted">Ort: {mangel.ort}</p>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Safety Concerns */}
        {report.sicherheitsbedenken && report.sicherheitsbedenken.length > 0 && (
          <div className="card-elevated p-4 bg-amber-50 border-amber-200">
            <h3 className="text-lg font-bold text-amber-800 mb-3">
              Sicherheitsbedenken
            </h3>
            <ul className="space-y-2">
              {report.sicherheitsbedenken.map((bedenken, index) => (
                <li key={index} className="flex items-start gap-3">
                  <AlertTriangle size={20} className="flex-shrink-0 mt-0.5 text-amber-700" />
                  <span className="text-amber-900">{bedenken}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Maintenance Recommendations */}
        {report.wartungsempfehlungen && report.wartungsempfehlungen.length > 0 && (
          <div className="card-elevated p-4">
            <h3 className="text-lg font-bold text-secondary mb-3">
              Wartungsempfehlungen
            </h3>
            <ul className="space-y-2">
              {report.wartungsempfehlungen.map((empfehlung, index) => (
                <li key={index} className="flex items-start gap-3">
                  <Wrench size={20} className="flex-shrink-0 mt-0.5 text-blue-500" />
                  <span>{empfehlung}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Additional Remarks */}
        {report.bemerkungen && (
          <div className="card-elevated p-4">
            <h3 className="text-lg font-bold text-secondary mb-3">
              Bemerkungen
            </h3>
            <p className="text-text leading-relaxed">
              {report.bemerkungen}
            </p>
          </div>
        )}

        {/* Processed Transcript */}
        {inspection?.processedTranscript && (
          <div className="card-elevated p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-bold text-secondary">
                {t('detail.processed_transcript')}
              </h3>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => handleFeedback('processed', 'like')}
                  disabled={feedbackSubmitting === 'processed'}
                  className={`p-2 rounded-md transition-colors ${
                    processedFeedback === 'like'
                      ? 'bg-green-100 text-green-600'
                      : 'hover:bg-background text-text-muted'
                  }`}
                  title={t('detail.feedback_like')}
                >
                  {feedbackSubmitting === 'processed' ? (
                    <Loader2 size={18} className="animate-spin" />
                  ) : (
                    <ThumbsUp size={18} className={processedFeedback === 'like' ? 'fill-current' : ''} />
                  )}
                </button>
                <button
                  onClick={() => handleFeedback('processed', 'dislike')}
                  disabled={feedbackSubmitting === 'processed'}
                  className={`p-2 rounded-md transition-colors ${
                    processedFeedback === 'dislike'
                      ? 'bg-red-100 text-red-600'
                      : 'hover:bg-background text-text-muted'
                  }`}
                  title={t('detail.feedback_dislike')}
                >
                  <ThumbsDown size={18} className={processedFeedback === 'dislike' ? 'fill-current' : ''} />
                </button>
                <button
                  onClick={() => handleCopy(inspection.processedTranscript, 'processed')}
                  className="p-2 hover:bg-background rounded-md transition-colors"
                  title={t('common.copy')}
                >
                  {copiedField === 'processed' ? (
                    <Check size={18} className="text-success" />
                  ) : (
                    <Copy size={18} className="text-text-muted" />
                  )}
                </button>
              </div>
            </div>
            <hr className="border-border mb-3" />
            <div className="max-h-96 overflow-y-auto prose prose-sm max-w-none text-text prose-headings:text-text prose-headings:font-bold prose-p:text-text prose-strong:text-text prose-ul:text-text prose-ol:text-text prose-li:text-text">
              <ReactMarkdown remarkPlugins={[remarkBreaks]}>{inspection.processedTranscript}</ReactMarkdown>
            </div>
          </div>
        )}
      </main>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        title={t('list.delete_confirm')}
        actions={
          <>
            <button onClick={handleDeleteCancel} className="btn-secondary">
              {t('common.cancel')}
            </button>
            <button
              onClick={handleDeleteConfirm}
              className="btn-primary bg-error hover:bg-error-dark"
            >
              {t('common.delete')}
            </button>
          </>
        }
      >
        {inspection && t('list.delete_message', { vehicleId: inspection.vehicleId })}
      </Dialog>
    </div>
  );
};

export default InspectionDetail;
